export interface CaseStudy {
  id: string;
  label: string;
  title: string;
  subtitle: string;
  logo: string;
  logoAlt: string;
  videos: { src: string; title: string }[];
  videoPoster: string;
  mediaImage: string;
  mediaImageAlt: string;
  heading: string;
  paragraphs: string[];
  statistic?: { value: string; description: string };
  images: { src: string; alt: string }[];
}

export const categories = {
  content: { label: "Content & Socials" },
  influencer: { label: "Influencer" },
  digital: { label: "Digital" },
  retail: { label: "Retail" },
  b2b: { label: "B2B" },
  experience: { label: "Experience" },
};

export type Category = keyof typeof categories;
export type CaseStudies = Record<Category, CaseStudy[]>;

interface PublishedStudy {
  id: string;
  displayOrder: number;
  title: string;
  description?: string;
  logo?: { url: string; alt: string };
  videoUrl?: string;
  videoHeroImage?: string;
  videoHeroImageAlt?: string;
  content?: { title?: string; text?: string };
  keyResult?: { value: string; description: string };
  images?: { url: string; alt: string }[];
}

export function videoEmbed(value: string): string {
  const url = new URL(value);
  if (
    url.protocol !== "https:" ||
    !["vimeo.com", "www.vimeo.com", "player.vimeo.com"].includes(url.hostname)
  )
    throw new Error("Unsupported video URL");
  if (url.hostname === "player.vimeo.com") return url.href;
  const parts = url.pathname.split("/").filter(Boolean);
  const id = parts.find((part) => /^\d+$/.test(part));
  if (!id) throw new Error("Invalid Vimeo video");
  const embed = new URL(`https://player.vimeo.com/video/${id}`);
  const hash = url.searchParams.get("h") || parts[parts.indexOf(id) + 1];
  if (hash) embed.searchParams.set("h", hash);
  return embed.href;
}

export function isVimeoUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      ["vimeo.com", "www.vimeo.com", "player.vimeo.com"].includes(url.hostname)
    );
  } catch (_) {
    return false;
  }
}

export function parseCaseStudies(value: unknown): CaseStudies {
  const snapshot = value as {
    schemaVersion: number;
    categories: Record<Category, PublishedStudy[]>;
  };
  if (!snapshot || snapshot.schemaVersion !== 1 || !snapshot.categories)
    throw new Error("Invalid case-study response");
  const result = {} as CaseStudies;
  for (const key of Object.keys(categories) as Category[]) {
    // Snapshots published before the B2B launch contain the original five
    // arrays. Treat only that newly introduced array as empty during rollout;
    // all established categories remain required.
    const rows =
      key === "b2b" && snapshot.categories[key] === undefined
        ? []
        : snapshot.categories[key];
    if (!Array.isArray(rows)) throw new Error("Missing case-study category");
    result[key] = [...rows]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((row) => {
        if (
          !row.id ||
          typeof row.title !== "string" ||
          !Number.isInteger(row.displayOrder)
        )
          throw new Error("Invalid case study");
        const mediaUrl =
          typeof row.videoUrl === "string" ? row.videoUrl.trim() : "";
        const heroImage =
          typeof row.videoHeroImage === "string"
            ? row.videoHeroImage.trim()
            : "";
        const isVimeo = Boolean(mediaUrl) && isVimeoUrl(mediaUrl);
        // Preserve snapshots made before the dedicated hero-image column:
        // a non-Vimeo URL in the former mixed media field remains an image.
        const displayImage = heroImage || (isVimeo ? "" : mediaUrl);
        return {
          id: row.id,
          label: categories[key].label,
          title: row.title,
          subtitle: row.description || "",
          logo: row.logo?.url || "",
          logoAlt: row.logo?.alt || "",
          videos: isVimeo
            ? [
                {
                  src: videoEmbed(mediaUrl),
                  title: row.title,
                },
              ]
            : [],
          videoPoster: isVimeo ? heroImage : "",
          mediaImage: isVimeo ? "" : displayImage,
          mediaImageAlt:
            displayImage &&
            !isVimeo &&
            typeof row.videoHeroImageAlt === "string"
              ? row.videoHeroImageAlt
              : "",
          heading: row.content?.title || "",
          paragraphs: (row.content?.text || "")
            .split(/\r?\n\s*\r?\n/)
            .map((text) => text.trim())
            .filter(Boolean),
          statistic: row.keyResult,
          // Images are optional content. Ignore empty or malformed entries so
          // a stale alt-text cell or an incomplete image pair cannot produce a
          // broken gallery item on the website.
          images: (Array.isArray(row.images) ? row.images : [])
            .filter(
              (image) =>
                image &&
                typeof image.url === "string" &&
                Boolean(image.url.trim())
            )
            .map((image) => ({
              src: image.url.trim(),
              alt: typeof image.alt === "string" ? image.alt : "",
            })),
        };
      });
  }
  return result;
}

// Public read endpoint only. The publishing token never belongs in the browser.
export const caseStudiesApi =
  process.env.VUE_APP_CASE_STUDIES_API_URL ||
  "https://catalyst-case-studies-api.catalystcreative.workers.dev/case-studies";
